import { getUploadBaseUrl } from '../api/uploadConfig';

export const decodeHtmlEntitiesInUrl = (value = '') => {
  const raw = String(value || '').trim();
  if (!raw) return '';
  return raw
    .replace(/&amp;/gi, '&')
    .replace(/&quot;/gi, '"')
    .replace(/&#0*39;/gi, "'")
    .replace(/&apos;/gi, "'")
    .replace(/&#x27;/gi, "'")
    .replace(/&#x2F;/gi, '/');
};

const NEWS_KADABA_DEFAULT_IMAGE_PATTERN =
  /\b(defualt|default-image|defaultimage|no-image|fallback-image|news-kadaba-defualt-image)\b/i;

const getPublisherImageProxyApiBase = () => {
  const uploadBase = String(getUploadBaseUrl() || '').replace(/\/$/, '');
  if (!uploadBase) return '';
  return `${uploadBase}/api`;
};

/** Unwrap `/sources/proxy-image?url=` back to the publisher asset. */
export const unwrapProxiedPublisherImageUrl = (imageUrl = '') => {
  const raw = decodeHtmlEntitiesInUrl(String(imageUrl || '').trim());
  if (!raw) return '';
  try {
    const parsed = new URL(raw, typeof window !== 'undefined' ? window.location.origin : 'http://localhost');
    if (/\/sources\/proxy-image$/i.test(parsed.pathname) && parsed.searchParams.get('url')) {
      return String(parsed.searchParams.get('url') || '').trim();
    }
  } catch {
    // keep original
  }
  return raw;
};

export const isNewsKadabaHostImageUrl = (imageUrl = '') => {
  const inner = unwrapProxiedPublisherImageUrl(imageUrl);
  try {
    const host = new URL(inner).hostname.replace(/^www\./i, '').toLowerCase();
    return host === 'newskadaba.com';
  } catch {
    return /newskadaba\.com/i.test(inner);
  }
};

export const isNewsKadabaDefaultImageUrl = (imageUrl = '') => {
  const inner = unwrapProxiedPublisherImageUrl(imageUrl);
  return isNewsKadabaHostImageUrl(inner) && NEWS_KADABA_DEFAULT_IMAGE_PATTERN.test(inner);
};

/**
 * Gamer Network image CDNs (images.purexbox.com, etc.) often challenge
 * no-referrer hotlinks. Prefer sending a normal cross-origin referrer.
 */
export const shouldOmitNoReferrerForImageUrl = (imageUrl = '') => {
  const raw = String(imageUrl || '').trim();
  if (/\/sources\/proxy-image\?/i.test(raw) || /wsrv\.nl|weserv\.nl/i.test(raw)) {
    return true;
  }
  try {
    const host = new URL(raw).hostname.toLowerCase();
    return (
      /^images\.(purexbox|nintendolife|pushsquare|purenintendo|purepcgames)\.com$/i.test(host) ||
      host.includes('proxy.duckduckgo.com') ||
      host.endsWith('.gadgethacks.com') ||
      host === 'assets.content.technologyadvice.com' ||
      host.replace(/^www\./, '') === 'newskadaba.com'
    );
  } catch {
    return false;
  }
};

/** Cloudflare-challenged / hotlink-fragile publisher thumbs → a browser-safe URL. */
export const toBrowsablePublisherImageUrl = (imageUrl = '') => {
  const raw = decodeHtmlEntitiesInUrl(String(imageUrl || '').trim());
  if (!/^https?:\/\//i.test(raw)) return raw;
  try {
    const parsed = new URL(raw);
    if (parsed.hostname.includes('proxy.duckduckgo.com')) return raw;
    if (/wsrv\.nl|weserv\.nl/i.test(parsed.hostname)) return raw;
    if (/\/sources\/proxy-image$/i.test(parsed.pathname)) return raw;

    const host = parsed.hostname.replace(/^www\./i, '').toLowerCase();
    if (host === 'newskadaba.com') {
      const apiBase = getPublisherImageProxyApiBase();
      if (apiBase) {
        return `${apiBase}/sources/proxy-image?url=${encodeURIComponent(raw)}`;
      }
    }

    if (!/^images\.(purexbox|nintendolife|pushsquare|purenintendo|purepcgames)\.com$/i.test(parsed.hostname)) {
      return raw;
    }
    return `https://proxy.duckduckgo.com/iu/?u=${encodeURIComponent(raw)}`;
  } catch {
    return raw;
  }
};

/** Path/query width hints that are too small for a Top Stories hero. */
const LOW_RES_WIDTHS = new Set([24, 48, 72, 96, 120, 144, 150, 180, 200, 220, 240, 250, 300, 320, 360, 400, 480, 500, 540, 640]);

/** Unwrap NPR Brightspot dims proxy (?url=...) to the underlying image asset. */
export const unwrapImageProxyUrl = (imageUrl = '') => {
  const raw = decodeHtmlEntitiesInUrl(imageUrl).trim();
  if (!raw || !/^https?:\/\//i.test(raw)) return raw;
  try {
    const parsed = new URL(raw);
    const host = parsed.hostname.toLowerCase();
    const path = parsed.pathname.toLowerCase();
    const isProxy =
      host.includes('brightspotcdn.com') ||
      host.includes('npr-brightspot.s3.amazonaws.com') ||
      /\/dims\d*\//i.test(path);
    if (!isProxy) return raw;
    const nestedRaw = parsed.searchParams.get('url');
    if (!nestedRaw) return raw;
    let nested = String(nestedRaw).trim();
    if (nested.startsWith('//')) nested = `https:${nested}`;
    if (/^http:\/\//i.test(nested)) nested = nested.replace(/^http:\/\//i, 'https://');
    if (/^https?:\/\//i.test(nested) && /\.(jpe?g|png|webp|gif|avif)(?:$|[?#])/i.test(nested)) {
      return nested;
    }
    return raw;
  } catch {
    return raw;
  }
};

/**
 * Guardian Fastly IO URLs (i.guim.co.uk?...&s=...) are HMAC-signed for exact
 * query params. Rewriting width/quality breaks the signature (401). Convert to
 * the stable media.guim.co.uk origin asset instead.
 */
export const stabilizeGuardianImageUrl = (imageUrl = '') => {
  const raw = decodeHtmlEntitiesInUrl(imageUrl).trim();
  if (!raw || !/^https?:\/\//i.test(raw)) return raw;

  try {
    const parsed = new URL(raw);
    const host = parsed.hostname.toLowerCase();
    if (!host.includes('guim.co.uk')) return raw;

    const pathMatch = parsed.pathname.match(
      /^\/(?:img\/media\/)?([a-f0-9]{20,})\/([^/]+)\/(?:master\/)?([^/]+\.(?:jpe?g|png|webp|gif))$/i
    );
    if (!pathMatch) return raw;

    const [, id, crop, file] = pathMatch;
    return `https://media.guim.co.uk/${id}/${crop}/${file}`;
  } catch {
    return raw;
  }
};

/**
 * News-Medical `/image-handler/` assets 404 unless they include `-WIDTHxHEIGHT`
 * (typically `-620x480` from og:image). Do not treat that as a WordPress thumb.
 */
export const stabilizeEspnImageUrl = (imageUrl = '') => {
  const raw = decodeHtmlEntitiesInUrl(imageUrl).trim();
  if (!raw || !/^https?:\/\//i.test(raw)) return raw;

  try {
    const parsed = new URL(raw);
    const host = parsed.hostname.toLowerCase().replace(/^www\./, '');
    if (host !== 'espncdn.com' && !host.endsWith('.espncdn.com')) return raw;
    if (/espn_logos|espn_red(?:\.png)?|espn_dotcom|teamlogos|espnicons/i.test(`${parsed.pathname} ${raw}`)) {
      return raw;
    }

    const nested = parsed.searchParams.get('img');
    if (nested) {
      let path = decodeURIComponent(nested).trim();
      if (path.startsWith('//')) path = `https:${path}`;
      if (/^https?:\/\//i.test(path)) return stabilizeEspnImageUrl(path);
      if (!path.startsWith('/')) path = `/${path}`;
      if (/espn_logos|espn_red(?:\.png)?|espn_dotcom|teamlogos/i.test(path)) return raw;
      return `https://a.espncdn.com${path}`;
    }

    return parsed.toString();
  } catch {
    return raw;
  }
};

export const stabilizeNewsMedicalImageUrl = (imageUrl = '') => {
  const raw = decodeHtmlEntitiesInUrl(imageUrl).trim();
  if (!raw || !/^https?:\/\//i.test(raw)) return raw;

  try {
    const parsed = new URL(raw);
    const host = parsed.hostname.toLowerCase().replace(/^www\./, '');
    if (host !== 'news-medical.net') return raw;
    if (!/\/image-handler\//i.test(parsed.pathname)) return raw;
    if (/-\d{2,4}x\d{2,4}\.(?:jpe?g|png|webp|gif)$/i.test(parsed.pathname)) {
      return parsed.toString();
    }
    if (!/\.(?:jpe?g|png|webp|gif)$/i.test(parsed.pathname)) return raw;

    parsed.pathname = parsed.pathname.replace(
      /\.(jpe?g|png|webp|gif)$/i,
      '-620x480.$1'
    );
    return parsed.toString();
  } catch {
    return raw;
  }
};

/**
 * Upgrade known CDN thumbnail URLs to a higher resolution variant.
 * BBC RSS only ships media:thumbnail at /240/ — rewrite to /976/.
 */
export const upgradeLowResolutionImageUrl = (imageUrl = '') => {
  const raw = unwrapImageProxyUrl(decodeHtmlEntitiesInUrl(imageUrl)).trim();
  if (!raw || !/^https?:\/\//i.test(raw)) return raw;

  const guardianStable = stabilizeGuardianImageUrl(raw);
  if (guardianStable !== raw) return guardianStable;

  const newsMedicalStable = stabilizeNewsMedicalImageUrl(raw);
  if (newsMedicalStable !== raw) return newsMedicalStable;

  const espnStable = stabilizeEspnImageUrl(raw);
  if (espnStable !== raw) return espnStable;

  try {
    const parsed = new URL(raw);
    const host = parsed.hostname.toLowerCase();
    // ABC WCMS crop URLs rely on impolicy + crop params — do not rewrite dims.
    if (host === 'abc-cdn.net.au' || host.endsWith('.abc-cdn.net.au')) {
      return raw;
    }
    let path = parsed.pathname;
    let changed = false;

    // Never mutate signed resize query strings — invalidates HMAC signatures.
    const hasSignedQuery = parsed.searchParams.has('s');
    const isNewsMedicalHandler =
      host.replace(/^www\./, '') === 'news-medical.net' &&
      /\/image-handler\//i.test(path);

    // BBC ichef: /ace/standard/240/... → /ace/standard/976/...
    if (host.includes('ichef.bbci.co.uk') || host.includes('bbcimg.co.uk') || host.includes('bbci.co.uk')) {
      const bbcRewritten = path
        .replace(/\/ace\/standard\/(\d{2,4})\//i, (_, width) => {
          const w = Number(width);
          if (LOW_RES_WIDTHS.has(w) || w < 800) {
            changed = true;
            return '/ace/standard/976/';
          }
          return `/ace/standard/${width}/`;
        })
        .replace(/\/ace\/ws\/(\d{2,4})\//i, (_, width) => {
          const w = Number(width);
          if (LOW_RES_WIDTHS.has(w) || w < 800) {
            changed = true;
            return '/ace/ws/976/';
          }
          return `/ace/ws/${width}/`;
        })
        .replace(/\/news\/(\d{2,4})\//i, (_, width) => {
          const w = Number(width);
          if (LOW_RES_WIDTHS.has(w) || w < 800) {
            changed = true;
            return '/news/976/';
          }
          return `/news/${width}/`;
        })
        .replace(/\/images\/(\d{2,4})\//i, (_, width) => {
          const w = Number(width);
          if (LOW_RES_WIDTHS.has(w) || w < 800) {
            changed = true;
            return '/images/976/';
          }
          return `/images/${width}/`;
        });
      path = bbcRewritten;
    }

    // WordPress-style "-240x135.jpg" → original ".jpg"
    // News-Medical image-handler requires the WxH suffix — do not strip.
    if (!isNewsMedicalHandler) {
      path = path.replace(/-(\d{2,4})x(\d{2,4})(?=\.(?:jpe?g|png|webp|gif)$)/i, (_, w, h) => {
        const width = Number(w);
        const height = Number(h);
        if ((Number.isFinite(width) && width <= 640) || (Number.isFinite(height) && height <= 360)) {
          changed = true;
          return '';
        }
        return `-${w}x${h}`;
      });
    }

    // Common resize query params (skip when URL carries a signature)
    if (!hasSignedQuery) {
      for (const key of ['w', 'width', 'h', 'height', 'maxwidth', 'max_width']) {
        if (!parsed.searchParams.has(key)) continue;
        const value = Number(parsed.searchParams.get(key));
        if (!Number.isFinite(value)) continue;
        if (key.startsWith('h') && value > 0 && value < 500) {
          parsed.searchParams.set(key, '800');
          changed = true;
        } else if (!key.startsWith('h') && value > 0 && value < 800) {
          parsed.searchParams.set(key, '1200');
          changed = true;
        }
      }
    }

    if (!changed && path === parsed.pathname) return raw;
    parsed.pathname = path;
    return parsed.toString();
  } catch {
    return raw;
  }
};

/** Rough score so higher-resolution CDN/path variants win over thumbnails. */
export const scoreImageUrlResolution = (imageUrl = '') => {
  const value = String(imageUrl || '').toLowerCase();
  if (!value) return -1000;
  let score = 0;

  if (/thumbnail|\/thumb(?:nail)?s?\/|\/small\/|\/tiny\//i.test(value)) score -= 250;
  if (/\/(og|original|full|large|xlarge|master)\b/i.test(value)) score += 400;

  const pathWidth = value.match(/\/(?:ace\/(?:standard|ws)|news|images|img)\/(\d{2,4})\//i);
  if (pathWidth) score += Number(pathWidth[1]) || 0;

  const queryWidth = value.match(/[?&](?:w|width)=(\d{2,4})\b/i);
  if (queryWidth) score += Number(queryWidth[1]) || 0;

  const wpSize = value.match(/-(\d{2,4})x(\d{2,4})\.(?:jpe?g|png|webp|gif)(?:[?#]|$)/i);
  if (wpSize) score += Math.max(Number(wpSize[1]) || 0, Number(wpSize[2]) || 0);

  return score;
};

export const isGenericNewsImageUrl = (imageUrl = '') => {
  if (!imageUrl || typeof imageUrl !== 'string') return false;

  try {
    const decoded = decodeHtmlEntitiesInUrl(imageUrl);
    const parsed = new URL(decoded);
    const path = parsed.pathname.toLowerCase();
    const fileName = path.split('/').pop() || '';
    const host = parsed.hostname.toLowerCase();
    const full = decoded.toLowerCase();

    if (/news\.google\.com/i.test(host)) return true;

    // DuckDuckGo image proxy wrapping a real publisher asset is valid.
    if (/proxy\.duckduckgo\.com/i.test(host) && parsed.searchParams.get('u')) {
      return isGenericNewsImageUrl(String(parsed.searchParams.get('u') || ''));
    }

    // Same-origin publisher image proxy — judge the inner News Kadaba/etc URL.
    if (/\/sources\/proxy-image$/i.test(path) && parsed.searchParams.get('url')) {
      return isGenericNewsImageUrl(String(parsed.searchParams.get('url') || ''));
    }

    // Publisher homepage / site-root URLs are not article thumbnails.
    if (!path || path === '/') return true;

    if (
      /googleusercontent\.com/i.test(host) &&
      /j6_cofbogxhri9im864nl_ligxvsqp2aupskei7z0cnnfdvgumwuy20nuuhkreqyrpy4beeibuc/i.test(full)
    ) {
      return true;
    }

    if (/googleusercontent\.com/i.test(host) && /\/google[_-]?news|\/news[_-]?stand/i.test(path)) {
      return true;
    }

    if (/gstatic\.com/i.test(host) && /\/(images\/branding|favicons?|google[_-]?news)/i.test(path)) {
      return true;
    }

    if (/images\.unsplash\.com\/photo-1504711434969/i.test(full)) {
      return true;
    }

    // NPR content:encoded tracking beacon — not an article image.
    if (
      /npr-rss-pixel/i.test(full) ||
      /\/include\/images\/tracking\//i.test(path) ||
      (/media\.npr\.org/i.test(host) && /pixel/i.test(path))
    ) {
      return true;
    }

    if (
      /espncdn\.com/i.test(host) &&
      /espn_logos|espn_red(?:\.png)?|espn_dotcom|teamlogos|espnicons/i.test(full)
    ) {
      return true;
    }

    // Drupal/WordPress store real article images under these paths; never treat as generic.
    const isCmsFilePath =
      /\/sites\/[^/]+\/files\//.test(path) || /\/wp-content\/uploads\//.test(path);
    if (isCmsFilePath) return false;

    const genericFilePatterns = [
      /(^|[-_])logos?([-_.]|$)/,
      /(^|[-_])icons?([-_.]|$)/,
      /placeholder/,
      /(^|[-_])avatars?([-_.]|$)/,
      /^defaults?([-_.]|$)/,
      /(no[-_]?image|image[-_]?not[-_]?found|blank|spacer|pixel)\.(jpg|jpeg|png|webp|gif)$/i,
    ];

    const genericDirPatterns = [
      /\/logos?\//,
      /\/icons?\//,
      /\/placeholders?\//,
      /\/avatars?\//,
      /\/tracking\//,
    ];

    return (
      genericFilePatterns.some((pattern) => pattern.test(fileName)) ||
      genericDirPatterns.some((pattern) => pattern.test(path))
    );
  } catch {
    return false;
  }
};

export const collectArticleImageCandidates = (article = {}) => {
  const fromArray = Array.isArray(article?.image_urls) ? article.image_urls : [];
  const fromMedia = Array.isArray(article?.media)
    ? article.media.filter((file) => typeof file === 'string' && !file.toLowerCase().endsWith('.mp4'))
    : [];
  const fallback = [article?.image_url, article?.imageUrl, article?.image].filter(
    (item) => typeof item === 'string' && item.trim()
  );

  const seen = new Set();
  const candidates = [...fromArray, ...fromMedia, ...fallback]
    .map((value) => upgradeLowResolutionImageUrl(unwrapImageProxyUrl(decodeHtmlEntitiesInUrl(value))))
    .filter((value) => value && !isGenericNewsImageUrl(value))
    .filter((value) => {
      if (seen.has(value)) return false;
      seen.add(value);
      return true;
    });

  const sorted = candidates.sort((a, b) => scoreImageUrlResolution(b) - scoreImageUrlResolution(a));
  if (
    sorted[0] &&
    (/npr\.org|brightspotcdn\.com|npr-brightspot/i.test(sorted[0]) ||
      /npr/i.test(String(article?.source?.name || article?.source || '')))
  ) {
    console.log(`[NPR] frontend image selected: ${sorted[0]}`);
  }
  return sorted;
};

export const getBestArticleImage = (article = {}) => collectArticleImageCandidates(article)[0] || '';

export const extractGoogleNewsArticleId = (url = '') => {
  const value = String(url || '');
  const match = value.match(/\/articles\/([^?/]+)/) || value.match(/\/read\/([^?/]+)/);
  return match ? match[1] : '';
};

export const getArticleImageLookupParams = (article = {}) => {
  const articleUrl = String(article?.url || article?.link || '').trim();
  const articleId =
    String(article?.googleNewsArticleId || '').trim() ||
    extractGoogleNewsArticleId(articleUrl);

  return { articleUrl, articleId };
};
